/** Ролики: превью, статус, дата и срок хранения. */
import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import type { ProjectStatus } from '@reels/shared';
import { ActionButton } from '../components/ActionButton';
import { EmptyState, ErrorState, Loading, OfflineBanner } from '../components/States';
import * as telegram from '../lib/telegram';
import { formatDuration } from '../lib/video';
import { useActions } from '../state/actions';
import { useStore } from '../state/store';
import type { ProjectSummary } from '../lib/types';
import { formatDate as formatLocaleDate, t } from '../i18n';

const statusLabel = (status: ProjectStatus): string => t(`projects.status.${status}`);

function statusClass(status: ProjectStatus): string {
  if (status === 'done') return 'status done';
  if (status === 'rendering') return 'status rendering';
  if (status === 'failed' || status === 'expired') return 'status expired';
  if (status === 'draft') return 'status';
  return 'status work';
}

function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return formatLocaleDate(date, { day: 'numeric', month: 'long' });
}

function expiryNote(project: ProjectSummary): string {
  if (!project.expiresAt) return '';
  const expires = new Date(project.expiresAt);
  if (Number.isNaN(expires.getTime())) return '';
  const days = Math.ceil((expires.getTime() - Date.now()) / (24 * 3600 * 1000));
  if (days <= 0) return t('projects.expiry.expired');
  if (days === 1) return t('projects.expiry.oneDay');
  return t('projects.expiry.left', { n: days });
}

export function Projects(): ReactElement {
  const { state, dispatch } = useStore();
  const actions = useActions();
  const [editing, setEditing] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [failedThumbs, setFailedThumbs] = useState<Record<string, string>>({});

  // Список обновляется один раз при открытии экрана; повтор — кнопкой в ошибке.
  const requested = useRef(false);
  useEffect(() => {
    if (requested.current) return;
    requested.current = true;
    void actions.refreshProjects();
  }, [actions]);

  const items = state.projects ?? [];

  // Как вкладка «Монтаж»: черновик остаётся, готовый или идущий проект уступает место новому.
  const startNew = (): void => {
    if (state.project && state.project.status !== 'draft') {
      dispatch({ type: 'project/set', project: null });
      dispatch({ type: 'uploads/clear' });
    }
    dispatch({ type: 'route', screen: 'editor' });
  };

  return (
    <>
      <h1>{t('projects.title')}</h1>
      {!state.online ? <OfflineBanner /> : null}

      {state.projectsLoading && items.length === 0 ? <Loading rows={3} text={t('projects.loading')} /> : null}

      {state.projectsError ? (
        <ErrorState message={state.projectsError} onRetry={() => void actions.refreshProjects()} />
      ) : null}

      {!state.projectsLoading && !state.projectsError && items.length === 0 ? (
        <EmptyState
          title={t('projects.empty.title')}
          hint={t('projects.empty.hint')}
        />
      ) : null}

      {items.map((project) => (
        <div key={project.id} className="card project-card">
          <div className="row project-main">
            {project.thumbUrl && failedThumbs[project.id] !== project.thumbUrl ? (
              <img className="thumb" src={project.thumbUrl} alt="" loading="lazy" onError={() => setFailedThumbs(prev => ({ ...prev, [project.id]: project.thumbUrl! }))} />
            ) : (
              <div className="thumb">🎬</div>
            )}
            <div className="col grow">
              <div className="project-head">
                <button type="button" className="project-title" aria-label={t('projects.rename.aria', { name: project.title ?? t('projects.untitled') })}
                  disabled={saving} onClick={() => { setTitle(project.title ?? ''); setEditing(project.id); }}>
                  <strong className="ellipsis">{project.title ?? t('projects.untitled')}</strong>
                </button>
                <span className={statusClass(project.status)}>{statusLabel(project.status)}</span>
              </div>
              <span className="small muted">
                {formatDate(project.createdAt)}
                {project.durationS ? ` · ${formatDuration(project.durationS)}` : ''}
              </span>
              <span className="small muted">{expiryNote(project)}</span>
            </div>
          </div>
          {editing === project.id ? (
            <form className="project-rename" onSubmit={event => {
              event.preventDefault();
              if (!title.trim() || saving) return;
              setSaving(true);
              void actions.renameProject(project.id, title.trim()).then(ok => { if (ok) setEditing(null); }).finally(() => setSaving(false));
            }}>
              <label htmlFor={`title-${project.id}`}>{t('projects.rename.label')}</label>
              <input id={`title-${project.id}`} autoFocus value={title} maxLength={120} required disabled={saving}
                onChange={event => setTitle(event.target.value)} />
              <div className="btn-row">
                <button className="btn small primary" type="submit" disabled={saving || !title.trim()}>{saving ? t('projects.saving') : t('projects.save')}</button>
                <button className="btn small" type="button" disabled={saving} onClick={() => setEditing(null)}>{t('projects.cancel')}</button>
              </div>
            </form>
          ) : null}
          <div className="btn-row">
            <button
              type="button"
              className="btn small grow"
              onClick={() => void actions.openProject(project.id)}
            >
              {t('projects.open')}
            </button>
            <button
              type="button"
              className="btn small danger"
              onClick={() => {
                void telegram.showConfirm(t('projects.delete.confirm')).then((ok) => {
                  if (ok) void actions.deleteProject(project.id);
                });
              }}
            >
              {t('projects.delete')}
            </button>
          </div>
        </div>
      ))}

      <ActionButton text={t('projects.new')} onClick={startNew} />
    </>
  );
}
